// Mirrors virtual-medic-backend/services/scoringCategoryService.js so the case
// editor shows the same categories and weights that marking applies.
export const SCORING_CATEGORY_KEYS = Object.freeze([
  'historyQuestions',
  'flow',
  'examinerQuestions',
  'empathy',
]);

const WEIGHT_PRECISION = 4;
const BALANCE_TOLERANCE = 0.001;

// Only the preset Flow and Empathy section types feed those categories. Custom
// sections always count as history questions, whatever their title says.
export const getSectionCategory = (sectionType) => {
  const normalized = (sectionType || '').toString().trim().toUpperCase();
  if (normalized === 'FLOW') return 'flow';
  if (normalized === 'EMPATHY') return 'empathy';
  return 'historyQuestions';
};

const hasPositiveMarks = (value) => Number(value) > 0;

export const getApplicableCategories = ({ sections = [], examinerQuestions = [] } = {}) => {
  const categories = new Set();
  (Array.isArray(sections) ? sections : []).forEach((section) => {
    const items = Array.isArray(section?.items) ? section.items : [];
    if (items.some((item) => hasPositiveMarks(item?.maxMarks))) {
      categories.add(getSectionCategory(section.sectionType));
    }
  });
  if ((Array.isArray(examinerQuestions) ? examinerQuestions : [])
    .some((question) => hasPositiveMarks(question?.maxMarks))) {
    categories.add('examinerQuestions');
  }
  return SCORING_CATEGORY_KEYS.filter((key) => categories.has(key));
};

const roundWeight = (value) => {
  const factor = 10 ** WEIGHT_PRECISION;
  return Math.round(value * factor) / factor;
};

const pickPositiveWeights = (source = {}, keys = []) => keys.reduce((acc, key) => {
  const numeric = Number(source?.[key]);
  acc[key] = Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  return acc;
}, {});

const sumWeights = (weights = {}) => Object.values(weights).reduce((total, value) => total + value, 0);

// Rescales weights so the applicable categories share 100% and the rest get 0.
export const resolveEffectiveWeights = (weights = {}, applicableCategories = SCORING_CATEGORY_KEYS, fallbackWeights = {}) => {
  const applicable = SCORING_CATEGORY_KEYS.filter((key) => applicableCategories.includes(key));
  let selected = pickPositiveWeights(weights, applicable);
  let total = sumWeights(selected);
  if (total <= 0) {
    selected = pickPositiveWeights(fallbackWeights, applicable);
    total = sumWeights(selected);
  }
  if (total <= 0) {
    selected = applicable.reduce((acc, key) => ({ ...acc, [key]: 1 }), {});
    total = applicable.length;
  }
  return SCORING_CATEGORY_KEYS.reduce((acc, key) => {
    acc[key] = applicable.includes(key) && total > 0 ? roundWeight(selected[key] / total) : 0;
    return acc;
  }, {});
};

export const sumApplicableWeights = (weights = {}, applicableCategories = []) => applicableCategories
  .reduce((total, key) => total + (Number(weights?.[key]) || 0), 0);

export const isWeightTotalBalanced = (total, applicableCategories = []) => (
  !applicableCategories.length || Math.abs(total - 1) < BALANCE_TOLERANCE
);

// Brings a case's scoring configuration in line with the categories it marks.
// Defaults are rescaled to the applicable categories. Case overrides are kept
// when already balanced; otherwise a newly included category starts from its
// default weight and the applicable weights are rescaled proportionally.
export const reconcileScoringConfig = (config, applicableCategories, previousApplicableCategories = null) => {
  if (!config) {
    return config;
  }
  const defaults = config.defaults || {};
  if (!config.overrides) {
    return {
      ...config,
      resolved: resolveEffectiveWeights(defaults, applicableCategories, defaults),
    };
  }

  // A case that marks nothing yet (an early draft) keeps its saved overrides.
  if (!applicableCategories.length) {
    return config;
  }

  const base = { ...defaults, ...config.overrides };
  const added = previousApplicableCategories
    ? applicableCategories.filter((key) => !previousApplicableCategories.includes(key))
    : [];
  added.forEach((key) => {
    if (!(Number(base[key]) > 0)) {
      base[key] = Number(defaults[key]) || 0;
    }
  });

  const excludedHaveWeight = SCORING_CATEGORY_KEYS
    .some((key) => !applicableCategories.includes(key) && Number(base[key]) > 0);
  const balanced = isWeightTotalBalanced(sumApplicableWeights(base, applicableCategories), applicableCategories);
  const resolved = !added.length && !excludedHaveWeight && balanced
    ? SCORING_CATEGORY_KEYS.reduce((acc, key) => {
      acc[key] = applicableCategories.includes(key) ? Number(base[key]) || 0 : 0;
      return acc;
    }, {})
    : resolveEffectiveWeights(base, applicableCategories, defaults);

  return {
    ...config,
    overrides: { ...resolved },
    resolved,
    source: 'case-overrides',
  };
};
